/**
 * workflowContainer.ts — Workflow 容器树投影类型
 *
 * 职责：
 *     容器节点、正文块、快照；对齐 SDK workflow_container_projector。
 *
 * 链路位置：
 *     workflowContainerProjector → workflowReducer（P1+）→ WorkflowChrome（P2+）。
 *
 * 当前裁剪范围：
 *     不含 React 视图 props；仅投影层 state。
 */

import type { WorkflowDisplayPayload } from './workflowDisplay';

export type WorkflowContainerScope =
  | 'workflow'
  | 'stage'
  | 'item'
  | 'branch'
  | 'aggregate'
  | 'subworkflow';

export type WorkflowContainerStatus =
  | 'pending'
  | 'running'
  | 'completed'
  | 'failed'
  | 'skipped'
  | 'cancelled'
  | 'blocked'
  | 'retrying';

export type ContentBlockKind = 'reasoning' | 'text' | 'tool';

export interface WorkflowContentBlock {
  kind: ContentBlockKind;
  preview: string;
  tool_name: string | null;
}

export interface WorkflowContainerNode {
  container_id: string;
  scope: WorkflowContainerScope;
  workflow_path: string;
  workflow_depth: number;
  scope_key: string;
  title: string;
  subtitle: string;
  status: WorkflowContainerStatus;
  pattern: string;
  parent_container_id: string | null;
  display: WorkflowDisplayPayload | null;
  content_blocks: WorkflowContentBlock[];
  is_open: boolean;
}

export interface WorkflowContainerTreeSnapshot {
  root_container_ids: string[];
  containers: Record<string, WorkflowContainerNode>;
  active_container_ids: string[];
}

/** reducer 运行时节点：投影字段 + loop 绑定（P1） */
export interface WorkflowContainerRuntimeNode extends WorkflowContainerNode {
  loopSessionId: string | null;
}

/** WorkflowSession 容器树 state（P1+） */
export interface WorkflowContainerTreeState {
  rootContainerIds: string[];
  activeContainerIds: string[];
  containersById: Record<string, WorkflowContainerRuntimeNode>;
}

export function createEmptyWorkflowContainerTreeState(): WorkflowContainerTreeState {
  return {
    rootContainerIds: [],
    activeContainerIds: [],
    containersById: {},
  };
}

export function containerTreeToSnapshot(
  tree: WorkflowContainerTreeState,
): WorkflowContainerTreeSnapshot {
  const containers: Record<string, WorkflowContainerNode> = {};
  for (const [id, node] of Object.entries(tree.containersById)) {
    const { loopSessionId: _loop, ...projection } = node;
    containers[id] = projection;
  }
  return {
    root_container_ids: [...tree.rootContainerIds],
    active_container_ids: [...tree.activeContainerIds],
    containers,
  };
}

export type ContainerAction = 'open' | 'close' | 'append-content';

/** SDK fixture expected.containers 子集（契约对照） */
export interface WorkflowContainerExpectedNode {
  title: string;
  subtitle: string;
  status: WorkflowContainerStatus;
  scope: WorkflowContainerScope;
  parent_container_id: string | null;
  content_blocks: WorkflowContentBlock[];
}

export interface SdkContainerFixtureExpected {
  root_container_ids: string[];
  active_container_ids: string[];
  containers: Record<string, WorkflowContainerExpectedNode>;
  cli_tree_contains?: string[];
}

export interface SdkContainerFixture {
  scenario: string;
  description?: string;
  events: Array<{
    event_type: string;
    data: Record<string, unknown>;
    step_index?: number | null;
    tool_name?: string | null;
    session_id?: string | null;
    timestamp?: number;
  }>;
  expected: SdkContainerFixtureExpected;
}
